// src/utils/relicVault/storeDeck.js
//
// Shuffle-bag draw for the store's relic slots. Draws without repeats until the
// pool of not-yet-shown, not-owned relics is exhausted, then reshuffles - so
// refreshing cycles through the catalog instead of re-rolling the same items.
// Ported from the designer's handoff (buildDeck / drawIds in lib/vaultApi.ts).

import { RELIC_CATALOG } from 'config/relicVault/relics';

/**
 * A freshly shuffled list of relic ids the player doesn't own. Ids in
 * `excludeIds` (the batch just shown) are pushed to the back so a reshuffle
 * doesn't immediately re-offer them.
 */
export function buildDeck(ownedIds, excludeIds) {
  const ownedSet = new Set(ownedIds);
  let avail = RELIC_CATALOG.filter(r => !ownedSet.has(r.id));
  if (avail.length === 0) avail = RELIC_CATALOG.slice();

  // Fisher-Yates
  for (let i = avail.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [avail[i], avail[j]] = [avail[j], avail[i]];
  }

  if (excludeIds && excludeIds.length) {
    const excludeSet = new Set(excludeIds);
    avail = avail
      .filter(r => !excludeSet.has(r.id))
      .concat(avail.filter(r => excludeSet.has(r.id)));
  }

  return avail.map(r => r.id);
}

/**
 * Draw `count` ids from `deck` starting at `pos`, skipping owned ids and
 * anything already drawn. Returns the ids plus the advanced deck/position.
 */
export function drawIds(deck, pos, count, ownedIds, lastBatch = []) {
  let d = deck.slice();
  let p = pos;
  const result = [];
  const ownedSet = new Set(ownedIds);
  let guard = 0;

  while (result.length < count && guard < 1000) {
    guard++;
    if (p >= d.length) {
      d = buildDeck(ownedIds, (lastBatch || []).concat(result));
      p = 0;
      if (d.length === 0) break;
    }
    const id = d[p];
    p++;
    if (!result.includes(id) && !ownedSet.has(id)) {
      result.push(id);
    }
  }

  // Every relic owned: fill the slots anyway so the store never renders empty.
  if (result.length < count) {
    const resultSet = new Set(result);
    for (let i = 0; i < RELIC_CATALOG.length && result.length < count; i++) {
      const id = RELIC_CATALOG[i].id;
      if (!resultSet.has(id)) {
        result.push(id);
        resultSet.add(id);
      }
    }
  }

  return { ids: result, deck: d, pos: p };
}

/** A first set of store slots for a player with `ownedIds`. */
export function initialStoreSlots(ownedIds, count) {
  const deck = buildDeck(ownedIds, null);
  return drawIds(deck, 0, count, ownedIds, []);
}
